import { Link, useLocation } from "react-router-dom";

/**
 * Friendly "sign in to view" card for public-facing resource pages that turn out to
 * need authentication — e.g. a shared skill link that resolves to a private skill.
 * (The API returns 404 for private / gated / genuinely-missing alike to avoid leaking
 * existence, so the page can't distinguish them — this card covers all three honestly.)
 *
 * Chosen over a hard redirect here because the visitor may be a newcomer following a
 * shared link: a bare login form gives no context, whereas this explains the situation
 * and offers both Log in and Sign up. Both links carry `?next=<current path>` so the
 * user lands back on this page after authenticating.
 *
 * @param title    Heading. Defaults to a generic "Sign in to view this".
 * @param message  Sub-text explaining why. Defaults to a private/unavailable notice.
 */
export default function AuthGate({
  title = "Sign in to view this",
  message = "This may be private or no longer available. Sign in to see it.",
}: {
  title?: string;
  message?: string;
}) {
  const location = useLocation();
  const next = encodeURIComponent(location.pathname + location.search);

  return (
    <main className="mx-auto flex min-h-[60vh] max-w-md flex-col items-center justify-center px-6 py-16 text-center">
      <div
        className="w-full rounded-2xl border border-[var(--border)] bg-[var(--bg-elev)] p-8"
        style={{ boxShadow: "var(--shadow-card)" }}
      >
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-primary/10 text-primary">
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" />
            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
          </svg>
        </div>
        <h1 className="font-display text-lg font-bold">{title}</h1>
        <p className="mx-auto mt-2 max-w-xs text-sm text-text/55">{message}</p>

        <div className="mt-6 flex items-center justify-center gap-2.5">
          <Link
            to={`/login?next=${next}`}
            className="rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-white shadow-btn transition hover:bg-primary-hover"
          >
            Log in
          </Link>
          <Link
            to={`/register?next=${next}`}
            className="rounded-lg border border-[var(--border)] px-5 py-2.5 text-sm font-semibold text-text/80 transition hover:border-primary/40"
          >
            Sign up
          </Link>
        </div>

        <Link to="/skills" className="mt-5 inline-block text-sm text-text/50 transition hover:text-text/80">
          ← Browse public skills
        </Link>
      </div>
    </main>
  );
}
